import React, { ReactElement, useEffect, useState } from "react";
import Avatar from "react-avatar";
import { useNavigate, useLocation } from "react-router-dom";
import { Logout } from "../../Components/Auth/isAuthenticated";
import { useT } from "../../i18n";
import { LanguageSwitcher } from "../../Components/LanguageSwitcher";
import {
  SidebarHeaderContainer,
  Sidebar,
  SidebarMenu,
  LogoArea,
  LogoMenu,
  LogoDivider,
  MenuContainer,
  MenuItemWrapper,
  MenuItemLabel,
  MenuItemIcon,
  SidebarFooter,
  SidebarFooterLink,
  HeaderContent,
  Header,
  ComponentContent,
  HideItemDesk,
} from "../Menu/styles";

// ─── Inline SVG Icons ──────────────────────────────────────────────────────────

const ic = (path: React.ReactNode) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {path}
  </svg>
);

const Icons = {
  Dashboard:       ic(<><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></>),
  Banners:         ic(<><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></>),
  Establishments:  ic(<><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>),
  Subscription:    ic(<><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></>),
  Notification:    ic(<><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></>),
  Profile:         ic(<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>),
  Support:         ic(<><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></>),
  Logout:          ic(<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>),
  Menu:            ic(<><line x1="3" y1="6"  x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>),
  Close:           ic(<><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>),
};

// ─── Menu items ────────────────────────────────────────────────────────────────

// Os labels são as CHAVES de tradução — traduzidos no render, já que aqui
// fora do componente não há acesso ao hook.
const MENU_ITEMS = [
  { path: "/home_establishment",             label: "Dashboard",              icon: Icons.Dashboard      },
  { path: "/establishment/banners",          label: "Banners",                icon: Icons.Banners        },
  { path: "/establishment/establishments",   label: "Meus Estabelecimentos",  icon: Icons.Establishments },
  { path: "/establishment/notification",     label: "Notificações Push",      icon: Icons.Notification   },
  { path: "/establishment/subscription",     label: "Assinatura",             icon: Icons.Subscription   },
  { path: "/establishment/profile",          label: "Meu Perfil",             icon: Icons.Profile        },
  { path: "/establishment/support",          label: "Suporte",                icon: Icons.Support        },
];

// ─── Mobile hook ───────────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [mobile, setMobile] = useState(window.innerWidth <= bp);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth <= bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return mobile;
}

// ─── Page label from path ──────────────────────────────────────────────────────
/** Devolve a CHAVE do rótulo; quem chama traduz. */
function getPageLabel(pathname: string): string {
  const match = MENU_ITEMS.find(
    item => pathname === item.path || pathname.startsWith(item.path + "/")
  );
  return match?.label ?? "Dashboard";
}

// ─── Component ─────────────────────────────────────────────────────────────────

interface Props {
  InternalComponent: ReactElement<any, any>;
}

const PageHeaderEstablishment: React.FC<Props> = ({ InternalComponent }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const isMobile  = useIsMobile();
  const t         = useT();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const getName = () => localStorage.getItem("fullName") || "Name";

  const Rota = (path: string) => {
    navigate(path, { replace: true });
    if (isMobile) setSidebarOpen(false); // fecha menu ao navegar no mobile
  };

  const LogOut = () => {
    Logout();
    navigate("/login", { replace: true });
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  // Bloqueia scroll do body quando sidebar está aberta no mobile
  useEffect(() => {
    document.body.style.overflow = (isMobile && sidebarOpen) ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isMobile, sidebarOpen]);

  return (
    <SidebarHeaderContainer>

      {/* ── Overlay backdrop (mobile only) ──────────────────────────────────── */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position:   "fixed",
            inset:      0,
            background: "rgba(0,0,0,0.45)",
            zIndex:     199,
            backdropFilter: "blur(1px)",
          }}
        />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <div style={isMobile ? {
        position:   "fixed",
        top:        0,
        left:       0,
        height:     "100vh",
        zIndex:     200,
        transform:  sidebarOpen ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.28s cubic-bezier(.4,0,.2,1)",
        willChange: "transform",
      } : {}}>
        <Sidebar>
          <SidebarMenu>

            <LogoArea>
              {/* Close button visible only on mobile */}
              {isMobile && (
                <button
                  onClick={() => setSidebarOpen(false)}
                  style={{
                    position:   "absolute",
                    top:        18,
                    right:      14,
                    background: "none",
                    border:     "none",
                    cursor:     "pointer",
                    color:      "#fff",
                    display:    "flex",
                    padding:    4,
                  }}
                >
                  {Icons.Close}
                </button>
              )}
              <LogoMenu
                src="/Pertalks2.png"
                alt="Pertalks"
                onClick={() => Rota("/home_establishment")}
              />
            </LogoArea>
            <LogoDivider />

            <MenuContainer>
              {MENU_ITEMS.map((item) => (
                <MenuItemWrapper
                  key={item.path}
                  active={isActive(item.path)}
                  onClick={() => Rota(item.path)}
                >
                  <MenuItemIcon>{item.icon}</MenuItemIcon>
                  <MenuItemLabel>{t(item.label)}</MenuItemLabel>
                </MenuItemWrapper>
              ))}
            </MenuContainer>

          </SidebarMenu>

          {/* ── Promo card: planos premium (Pulse / Wave) ──────────────────── */}
          <div style={{
            margin:        "12px 12px 8px",
            background:    "#fff",
            borderRadius:  16,
            padding:       "16px 14px",
          }}>
            <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 700, color: "#111", lineHeight: 1.3 }}>
              {t("Conheça os planos Pulse e Wave")}
            </p>
            <p style={{ margin: "0 0 12px", fontSize: 12, color: "#555", lineHeight: 1.5 }}>
              {t("Destaque seu estabelecimento com")}{" "}
              <strong style={{ color: "#111" }}>{t("espaço premium")}</strong>{" "}
              {t("no app.")}
            </p>
            <button
              onClick={() => Rota("/establishment/subscription")}
              style={{
                width:        "100%",
                padding:      "10px",
                background:   "#00b6bc",
                color:        "#fff",
                border:       "none",
                borderRadius: 30,
                fontWeight:   700,
                fontSize:     13,
                cursor:       "pointer",
              }}
            >
              {t("Assine agora mesmo")}
            </button>
          </div>

          {/* Seletor de idioma — só existe no painel do estabelecimento */}
          <LanguageSwitcher />

          <SidebarFooter>
            <SidebarFooterLink onClick={LogOut}>
              <MenuItemIcon>{Icons.Logout}</MenuItemIcon>
              <MenuItemLabel>{t("Sair")}</MenuItemLabel>
            </SidebarFooterLink>
          </SidebarFooter>
        </Sidebar>
      </div>

      {/* ── Main content ────────────────────────────────────────────────────── */}
      <HeaderContent>
        <Header>

          {/* Hamburger — mobile only */}
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(true)}
              style={{
                background: "none",
                border:     "none",
                cursor:     "pointer",
                color:      "#333",
                display:    "flex",
                alignItems: "center",
                padding:    "4px 6px",
              }}
            >
              {Icons.Menu}
            </button>
          )}

          {/* Page title — mobile only */}
          {isMobile && (
            <span style={{
              fontSize:   16,
              fontWeight: 700,
              color:      "#fff",
              position:   "absolute",
              left:       "50%",
              transform:  "translateX(-50%)",
            }}>
              {t(getPageLabel(location.pathname))}
            </span>
          )}

          {/* Avatar — always visible in header */}
          <HideItemDesk>
            <Avatar
              style={{ cursor: "pointer" }}
              onClick={() => Rota("/establishment/profile")}
              name={getName()}
              size="40"
              round={true}
              color="#b96ea5"
            />
          </HideItemDesk>

        </Header>

        {/* Content — remove min-width on mobile */}
        <div style={isMobile ? { margin: "16px 0 0", padding: "0 16px 48px", overflowX: "hidden" } : {}}>
          <ComponentContent style={isMobile ? { minWidth: 0, margin: 0, padding: 0 } : {}}>
            {InternalComponent}
          </ComponentContent>
        </div>

      </HeaderContent>

    </SidebarHeaderContainer>
  );
};

export default PageHeaderEstablishment;